import Dexie, { type EntityTable } from 'dexie';
import type {
  AppointmentMode,
  ConsentStatus,
  EnergySaleType,
  LeadEventKind,
  LeadStatus,
  LeadTelcoSale,
  Story,
  StoryKind,
  StoryLead,
  SyncState,
  UserRole,
} from '@doorly/shared';

/**
 * Embed du commercial sur le lead — renvoyé par l'API depuis mai 2026 pour
 * éviter une dépendance Dexie users dans le footer (peut être périmé).
 * Anonymisé pour les observers above-line par AnonymizeInterceptor.
 */
export interface LeadCommercialEmbed {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  matricule: string;
  partner: { id: string; name: string } | null;
}

/**
 * Local mirror of a Lead, plus client-only sync metadata.
 */
export interface LeadRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string | null;
  postalCode: string;
  city: string | null;
  /** ID BAN (Base Adresse Nationale) — null si saisie libre. */
  addrBanId?: string | null;
  /** Code INSEE de la commune (5 chars). */
  codeInsee?: string | null;
  addrLat?: number | null;
  addrLon?: number | null;
  partnerId: string;
  commercialId: string;
  /**
   * Snapshot du manager / superviseur DU VENDEUR au moment de la création
   * (colonnes `Lead.managerId` / `Lead.superviseurId` côté API). Sert au
   * filtre « par individu » de la liste : sélectionner un manager garde les
   * fiches de son équipe (snapshot) ET les siennes (`commercialId`).
   *
   * `undefined` = ligne Dexie écrite avant l'ajout du champ, ou fiche créée
   * hors ligne pas encore repassée par le serveur : elle ne matchera aucun
   * filtre hiérarchique jusqu'au prochain sync (le filtre est un outil de
   * pilotage, utilisé en ligne).
   */
  managerId?: string | null;
  superviseurId?: string | null;
  /**
   * Magasin où la fiche a été faite (réseaux de boutiques), figé à la
   * création. Hors ligne, c'est le magasin choisi sur la tablette ; le
   * serveur le revérifie et peut le corriger au sync. `store` = son nom,
   * embarqué par l'API.
   */
  storeId?: string | null;
  store?: { id: string; name: string } | null;
  status: LeadStatus;
  /** Raison de non-vente quand status='not_sold'. Strippé pour les
   *  non-admins par l'AnonymizeInterceptor → `undefined` côté front. */
  nonVenteReason?: 'ko_partenaire' | null;
  /** Raison d'annulation quand status='cancelled'. Admin-only. */
  cancellationReason?: 'consent_expired' | 'admin_manual' | 'vendor_manual' | null;
  /** Raison d'échec de livraison quand status='delivery_failed'. Le détail
   *  technique n'est affiché qu'aux admins ; `partner_duplicate` est lu par
   *  tous (pill « Doublon », cf. displayStatus). */
  deliveryFailedReason?:
    | 'quarantine_hopti'
    | 'partner_ws_error'
    | 'partner_refused'
    | 'partner_duplicate'
    | null;
  notes: string | null;
  /** Embed du commercial — facultatif pour les leads créés offline avant
      arrivée de la réponse serveur. */
  commercial?: LeadCommercialEmbed | null;
  // V2 verticale fields
  verticaleId?: string | null;
  deliveryPartnerId?: string | null;
  /** @deprecated Plus jamais écrits sur l'appareil (v10) — l'admin lit les montants en ligne. */
  prixVente?: number | null;
  /** @deprecated idem `prixVente`. */
  commission?: number | null;
  specificData?: Record<string, unknown> | null;
  contactGroupId?: string | null;
  civilite?: string | null;
  /** Mode de rendez-vous capturé dans le wizard ('immediate' = rappel
      direct, 'scheduled' = planifié à scheduledFor). Null pour les leads
      legacy avant la promotion. */
  appointmentMode?: AppointmentMode | null;
  /** Datetime du rappel planifié (ISO UTC). Null si appointmentMode ≠ scheduled. */
  scheduledFor?: string | null;
  /** Trace RGPD des consentements (table `lead_consents` côté API).
      Embarqué dans la réponse `/leads/:id` ; non renseigné dans la list
      pour ne pas alourdir le payload. */
  consents?: { id: string; position: number; label: string; acceptedAt: string }[] | null;
  /** Journal d'événements métier (table `lead_events` côté API). Sert
      à construire la timeline du LeadDetail. Filtré côté API selon le
      rôle (les events `adminOnly` sont strippés pour les non-admins).
      Non renseigné dans la list (payload réduit). */
  events?:
    | {
        id: string;
        kind: LeadEventKind;
        fromValue?: string | null;
        toValue?: string | null;
        actorUserId?: string | null;
        adminOnly: boolean;
        meta?: Record<string, unknown> | null;
        createdAt: string;
      }[]
    | null;
  /** Ventes TMK réconciliées (fibre/mobile). Renseigné dans la list ET le
      détail (contrairement à events/consents) pour l'affichage admin des
      pastilles sur la carte. Vide hors verticale Telco / non réconcilié. */
  telcoSales?: LeadTelcoSale[] | null;
  /** Type de vente énergie (Active Contact) : 'ELEC'/'GAZ'/'DUAL', dérivé du
      raw_label de la qualif de vente. Pendant "énergie" des pastilles TMK.
      Null hors verticale énergie / partenaire ≠ Active Contact. */
  energySaleType?: EnergySaleType | null;
  /** Statut de consentement RGPD. Default 'verified' (cas online).
      'pending_offline' = SMS différé après création hors-ligne, à valider
      a posteriori. 'expired' = délais dépassés, lead non transmis. */
  consentStatus?: ConsentStatus | null;
  /** Deadline saisie OTP (ISO). Null hors mode différé. */
  consentExpiresAt?: string | null;
  /** SmsLog id du SMS de consentement différé. Renseigné par l'API au sync. */
  consentSmsLogId?: string | null;
  version: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  _syncState: SyncState;
  _localOnly: boolean;
  _lastError?: string;
}

export interface MutationRow {
  id: string;
  /** `pin_switch` = déclaration d'une bascule vérifiée hors ligne (payload `PinSwitchReportInput`). */
  entity: 'lead' | 'pin_switch';
  op: 'create' | 'update';
  entityId: string;
  /**
   * Au nom de qui la mutation a été produite (session PIN). La file est
   * attachée à la TABLETTE, pas à l'utilisateur : sans ce champ, trois fiches
   * saisies hors ligne par A partaient sous l'identité de B après une bascule,
   * et l'API les refusait comme usurpation (spec §6.1). `null` = la session
   * elle-même (pas de bascule au moment de la saisie).
   */
  actorUserId?: string | null;
  payload: unknown;
  baseVersion: number;
  createdAt: string;
  attempts: number;
  nextAttemptAt: string;
  lastError?: string;
  status: 'queued' | 'in_flight' | 'failed' | 'dead';
}

export interface CachedUserRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  partnerId: string;
  matricule: string;
  /** Rattachement hiérarchique — alimente la cascade du filtre par individu
      (manager → superviseur → vendeur) sans réseau. */
  managerId?: string | null;
  superviseurId?: string | null;
  /** Le vendeur a posé un code PIN — alimente la liste de la modale « Changer d'utilisateur ». */
  hasPin?: boolean;
}

export interface CachedPartnerRow {
  id: string;
  name: string;
  matriculePrefix: string;
  /** Boutons Appeler / SMS (réglage réseau). Absent = ligne d'avant le réglage → affichés. */
  contactButtons?: boolean;
  /** Champ « Numéro de commande » du questionnaire Telco (réglage admin). Absent = masqué. */
  telcoOrderNumber?: boolean;
}

export interface CachedVerticaleRow {
  id: string;
  name: string;
  slug: string;
  priority: number;
  icon: string | null;
  // Pas de prix de vente ni de rétrocession ici : jamais stockés sur l'appareil
  // (purgés par la v10, cf. plus bas).
  arguments: string[];
  active: boolean;
}

/**
 * Une story du Fil, mise en cache pour que l'anneau et l'écran marchent hors
 * ligne. `seen` est un entier (0/1) et non un booléen : Dexie n'indexe pas
 * les booléens, et l'anneau est un `where('seen').equals(0)`.
 */
export interface CachedStoryRow {
  id: string;
  kind: StoryKind;
  at: string;
  leadId: string;
  seen: 0 | 1;
  /**
   * Tri par le vendeur (local, v1). `todo` = « rappelle-le-moi » (mise de
   * côté), gardé en tête du Fil jusqu'à ce qu'il le lève ; `dismissed` = « c'est vu », masqué — mais
   * il revient si le fait se répète (`at` avance). Absent = actif. Non indexé
   * (la table est petite) : pas de bump de version Dexie.
   */
  state?: 'todo' | 'dismissed';
  lead: StoryLead;
  detail?: Story['detail'];
}

/**
 * Vérificateur hors ligne d'un vendeur du magasin (hash de son PIN), reçu de
 * `GET /pin/verifiers` par la session du superviseur. Cf. `PinVerifierSchema`
 * pour ce que sa présence sur l'appareil implique — et pourquoi c'est
 * acceptable.
 */
export interface PinVerifierRow {
  userId: string;
  verifier: string;
}

/** Compteur d'échecs PIN local, par vendeur — l'équivalent hors ligne de `pin_attempts`. */
export interface PinLocalAttemptRow {
  userId: string;
  failures: number;
  lockedUntil: string | null;
}

/**
 * Magasins ouverts à l'APPAREIL (`GET /users/me/stores`, niveau tablette) :
 * une seule ligne, clé `device`. En session PIN ce sont ceux du superviseur
 * de la tablette. Sert au bandeau magasin et au choix du magasin, hors ligne
 * compris.
 */
export interface DeviceStoresRow {
  key: 'device';
  deviceUserId: string;
  retail: boolean;
  stores: { id: string; name: string; externalCode: string | null }[];
  /** Le réseau a au moins un magasin actif (sinon : rien ne change pour le terrain). */
  networkHasStores?: boolean;
  fetchedAt: string;
}

export interface CachedDeliveryPartnerRow {
  id: string;
  /** Nom interne admin (ex : "Orange (Armatis)"). Pas affiché côté prospect. */
  name: string;
  /** Marque côté prospect (ex : "Orange"). Préférée pour tout affichage
      visible par le vendeur ou le client final. Fallback sur `name` si null. */
  brand: string | null;
  /** Call-center qui traitera le lead (ex : "Armatis"). Affichage
      admin-only — info B2B interne, jamais montrée aux autres rôles. */
  callcenter: string | null;
  /** Numéro miroir : n° entrant dédié callcenter, composable par le
      prospect pour rejoindre directement le partenaire. Affiché sur la
      fiche tant que le statut n'est pas terminal. Null si non configuré. */
  mirrorPhone: string | null;
  /** Scope de routage Telco. null = wildcard (sert tous les scopes).
      Valeurs explicites : 'fibre_only' / 'mobile_only' / 'fibre_and_mobile'.
      Ignoré pour les partenaires d'autres verticales. */
  telcoProductScope: 'fibre_only' | 'mobile_only' | 'fibre_and_mobile' | null;
  revealText: string;
  verticaleId: string;
  weight: number;
  active: boolean;
  /** Heure d'ouverture du call-center, format "HH:MM" (24h). Null = pas
      d'horaires configurés → on tombe sur le fallback générique. */
  openingStart: string | null;
  /** Heure de fermeture, format "HH:MM" (24h). Le dernier créneau
      sélectionnable couvre [openingEnd-1h, openingEnd]. */
  openingEnd: string | null;
  /** Jours d'ouverture : 1=lundi, …, 6=samedi. Le dimanche n'est jamais
      autorisé (constante métier). Default `[1,2,3,4,5]` = lun-ven. */
  openingDays: number[];
  /** Quota journalier (reset minuit Europe/Paris). Null = illimité. */
  dailyQuota: number | null;
  /** Compteur lecture-seule fourni par l'API à chaque warm — leads créés
      depuis 00:00 Europe/Paris chez ce partenaire. Sert au pacing temporel
      et au hard cap dans `pickDeliveryPartner`. */
  deliveredToday: number;
}

export class DoorlyDB extends Dexie {
  stories!: EntityTable<CachedStoryRow, 'id'>;
  pinVerifiers!: EntityTable<PinVerifierRow, 'userId'>;
  pinAttempts!: EntityTable<PinLocalAttemptRow, 'userId'>;
  leads!: EntityTable<LeadRow, 'id'>;
  mutations!: EntityTable<MutationRow, 'id'>;
  users!: EntityTable<CachedUserRow, 'id'>;
  partners!: EntityTable<CachedPartnerRow, 'id'>;
  verticales!: EntityTable<CachedVerticaleRow, 'id'>;
  deliveryPartners!: EntityTable<CachedDeliveryPartnerRow, 'id'>;
  deviceStores!: EntityTable<DeviceStoresRow, 'key'>;

  constructor() {
    super('doorly');

    this.version(1).stores({
      leads: 'id, commercialId, status, _syncState, updatedAt',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      teams: 'id, partnerId, managerId',
    });

    this.version(2)
      .stores({
        leads: 'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId',
        mutations: 'id, status, nextAttemptAt, entity, entityId',
        users: 'id, role, partnerId',
        partners: 'id, name',
        teams: 'id, partnerId, managerId',
        verticales: 'id, slug, priority',
        deliveryPartners: 'id, verticaleId',
      })
      .upgrade((tx) => {
        return tx
          .table('leads')
          .toCollection()
          .modify((lead) => {
            lead.verticaleId = lead.verticaleId ?? null;
            lead.deliveryPartnerId = lead.deliveryPartnerId ?? null;
            lead.prixVente = lead.prixVente ?? null;
            lead.commission = lead.commission ?? null;
            lead.specificData = lead.specificData ?? null;
            lead.contactGroupId = lead.contactGroupId ?? null;
            lead.civilite = lead.civilite ?? null;
          });
      });

    // v3 : index `consentStatus` pour la live query du banner top
    // (`db.leads.where('consentStatus').equals('pending_offline')`).
    this.version(3)
      .stores({
        leads:
          'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
        mutations: 'id, status, nextAttemptAt, entity, entityId',
        users: 'id, role, partnerId',
        partners: 'id, name',
        teams: 'id, partnerId, managerId',
        verticales: 'id, slug, priority',
        deliveryPartners: 'id, verticaleId',
      })
      .upgrade((tx) => {
        return tx
          .table('leads')
          .toCollection()
          .modify((lead) => {
            lead.consentStatus = lead.consentStatus ?? 'verified';
            lead.consentExpiresAt = lead.consentExpiresAt ?? null;
            lead.consentSmsLogId = lead.consentSmsLogId ?? null;
          });
      });

    // v4 : ajout des champs matricule (User) et matriculePrefix (Partner).
    // Schéma identique côté index — on remet juste à zéro les tables users/
    // partners pour forcer un re-hydrate via CacheWarmer (matricule absent
    // des entrées v3 ferait planter les rendus qui le lisent).
    this.version(4)
      .stores({
        leads:
          'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
        mutations: 'id, status, nextAttemptAt, entity, entityId',
        users: 'id, role, partnerId',
        partners: 'id, name',
        teams: 'id, partnerId, managerId',
        verticales: 'id, slug, priority',
        deliveryPartners: 'id, verticaleId',
      })
      .upgrade(async (tx) => {
        await tx.table('users').clear();
        await tx.table('partners').clear();
      });

    // v5 : promotion `wizard.rdv` → colonnes `appointmentMode` / `scheduledFor`.
    // Backfill best-effort depuis l'ancien specificData.wizard.rdv (les leads
    // déjà synced auront leurs colonnes alimentées par le backend au prochain
    // refresh — ici on couvre les pending qui n'ont pas encore quitté Dexie).
    this.version(5)
      .stores({
        leads:
          'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
        mutations: 'id, status, nextAttemptAt, entity, entityId',
        users: 'id, role, partnerId',
        partners: 'id, name',
        teams: 'id, partnerId, managerId',
        verticales: 'id, slug, priority',
        deliveryPartners: 'id, verticaleId',
      })
      .upgrade((tx) => {
        return tx
          .table('leads')
          .toCollection()
          .modify((lead) => {
            const sd = lead.specificData as
              | { wizard?: { rdv?: { mode?: AppointmentMode; scheduledAt?: string | null } } }
              | null
              | undefined;
            const rdv = sd?.wizard?.rdv ?? null;
            if (lead.appointmentMode == null && rdv?.mode) lead.appointmentMode = rdv.mode;
            if (lead.scheduledFor == null && rdv?.scheduledAt) lead.scheduledFor = rdv.scheduledAt;
          });
      });

    // v6 : filtre « par individu » sur la liste des fiches — les snapshots
    // `managerId` / `superviseurId` du lead, et le rattachement hiérarchique
    // des users en cache (pour la cascade manager → superviseur → vendeur).
    //
    // Schéma d'index inchangé et AUCUN upgrade : les deux nouveaux champs sont
    // facultatifs et ne servent qu'au filtrage. On ne vide donc pas `users`
    // comme en v4 (où `matricule` manquant cassait des rendus) — un cache
    // vidé juste avant un lancement HORS LIGNE priverait l'app des noms de
    // vendeurs sur toutes les fiches, alors qu'ici l'absence des deux champs
    // ne coûte qu'une cascade vide jusqu'au prochain passage en ligne, où le
    // CacheWarmer réécrit les users et `useSyncedLeads` réécrit les fiches.
    this.version(6).stores({
      leads:
        'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      teams: 'id, partnerId, managerId',
      verticales: 'id, slug, priority',
      deliveryPartners: 'id, verticaleId',
    });

    // v7 : la notion d'équipe est retirée du produit — la table `teams`
    // disparaît du cache (`null` = suppression Dexie). Les autres tables sont
    // inchangées.
    this.version(7).stores({
      leads:
        'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      teams: null,
      verticales: 'id, slug, priority',
      deliveryPartners: 'id, verticaleId',
    });

    // v8 : le Fil — les nouveautés du vendeur, hydratées par le CacheWarmer.
    this.version(8).stores({
      leads:
        'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      verticales: 'id, slug, priority',
      deliveryPartners: 'id, verticaleId',
      stories: 'id, kind, at, seen',
    });

    // v9 : code PIN sur tablette partagée — vérificateurs hors ligne et compteur
    // d'échecs local. `mutations` gagne `actorUserId` (champ, pas index : on ne
    // filtre jamais dessus). Aucun upgrade : rien à migrer.
    this.version(9).stores({
      leads:
        'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      verticales: 'id, slug, priority',
      deliveryPartners: 'id, verticaleId',
      stories: 'id, kind, at, seen',
      pinVerifiers: 'userId',
      pinAttempts: 'userId',
    });

    // v10 (06/10/2026) : purge des montants. Prix de vente Hipto et
    // rétrocession réseau étaient servis à tous les rôles et stockés sur
    // chaque appareil (tablettes partagées comprises) — par verticale et
    // recopiés sur chaque fiche saisie. L'API ne les renvoie plus qu'aux
    // admins, et l'app ne les stocke plus du tout. Mêmes index que la v9.
    this.version(10)
      .stores({
        leads:
          'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
        mutations: 'id, status, nextAttemptAt, entity, entityId',
        users: 'id, role, partnerId',
        partners: 'id, name',
        verticales: 'id, slug, priority',
        deliveryPartners: 'id, verticaleId',
        stories: 'id, kind, at, seen',
        pinVerifiers: 'userId',
        pinAttempts: 'userId',
      })
      .upgrade(async (tx) => {
        await tx
          .table('verticales')
          .toCollection()
          .modify((v) => {
            delete v.prixVente;
            delete v.commission;
          });
        await tx
          .table('leads')
          .toCollection()
          .modify((lead) => {
            delete lead.prixVente;
            delete lead.commission;
          });
      });

    // v11 (09/10/2026) : magasins — les magasins ouverts à l'appareil (bandeau,
    // choix du magasin hors ligne). `leads.storeId` est un champ, pas un index.
    this.version(11).stores({
      leads:
        'id, commercialId, status, _syncState, updatedAt, verticaleId, contactGroupId, consentStatus',
      mutations: 'id, status, nextAttemptAt, entity, entityId',
      users: 'id, role, partnerId',
      partners: 'id, name',
      verticales: 'id, slug, priority',
      deliveryPartners: 'id, verticaleId',
      stories: 'id, kind, at, seen',
      pinVerifiers: 'userId',
      pinAttempts: 'userId',
      deviceStores: 'key',
    });
  }

  async wipe(): Promise<void> {
    await this.transaction(
      'rw',
      [
        this.leads,
        this.mutations,
        this.users,
        this.partners,
        this.verticales,
        this.deliveryPartners,
        this.stories,
        this.pinVerifiers,
        this.pinAttempts,
        this.deviceStores,
      ],
      async () => {
        await this.leads.clear();
        await this.mutations.clear();
        await this.users.clear();
        await this.partners.clear();
        await this.verticales.clear();
        await this.stories.clear();
        await this.deliveryPartners.clear();
        await this.pinVerifiers.clear();
        await this.pinAttempts.clear();
        await this.deviceStores.clear();
      },
    ).catch((err) => {
      console.warn('[db] wipe failed (ignored):', err);
    });
  }
}

export const db = new DoorlyDB();
